import { useEffect, useRef } from 'react';
import { styled } from '@mui/material';
import { handleApiError } from '@helpers/api';
import logger from '@widgets/logger';

const Container = styled('div')({
    '& .gmnoprint': {
        display: 'block !important',
    },
});

export const DEFAULT_MAP_CENTER = { lat: -33.8688, lng: 151.2195 };

export const resolveCoordinate = (value, fallback) =>
    value === '' || value === null || value === undefined ? fallback : +value;

const isValidCoordinate = center => center && Number.isFinite(center.lat) && Number.isFinite(center.lng);

export const findAddressPoint = async query => {
    try {
        const { PlacesService, PlacesServiceStatus } = await window.google.maps.importLibrary('places');
        return await new Promise(resolve =>
            new PlacesService(document.createElement('div')).findPlaceFromQuery(
                { query, fields: ['geometry'] },
                (results, status) => {
                    const location = status === PlacesServiceStatus.OK ? results?.[0]?.geometry?.location : null;
                    resolve(location ? { lat: location.lat(), lng: location.lng() } : null);
                },
            ),
        );
    } catch (error) {
        logger.error('Could not resolve the address to a point', error);
        return null;
    }
};

const createBadge = ({ label, color }) => {
    const badge = document.createElement('div');
    badge.textContent = label;
    badge.style.cssText = [
        'width: 28px',
        'height: 28px',
        'border-radius: 50%',
        `background: ${color}`,
        'color: #fff',
        'display: flex',
        'align-items: center',
        'justify-content: center',
        'font: 500 11px Rubik, sans-serif',
        'border: 2px solid #fff',
        'box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3)',
    ].join(';');
    return badge;
};

const GoogleMap = ({
    id,
    options,
    onMapLoad = () => {},
    style,
    onChange = () => {},
    pinDraggable = true,
    markers = null,
    onMarkerClick = null,
}) => {
    const mapRef = useRef(null);
    const markerRef = useRef(null);
    const centerRef = useRef(options.center);
    const draggedCenterRef = useRef(null);
    const badgeMarkersRef = useRef([]);
    const markerLibRef = useRef(null);
    const markersRef = useRef(markers);
    const onMarkerClickRef = useRef(onMarkerClick);
    const pinDraggableRef = useRef(pinDraggable);
    const fittedPositionsRef = useRef(null);

    centerRef.current = options.center;
    markersRef.current = markers;
    onMarkerClickRef.current = onMarkerClick;
    pinDraggableRef.current = pinDraggable;

    const renderBadgeMarkers = items => {
        const { AdvancedMarkerElement } = markerLibRef.current;
        badgeMarkersRef.current.forEach(marker => {
            marker.map = null;
        });
        badgeMarkersRef.current = items
            .filter(item => isValidCoordinate({ lat: item.lat, lng: item.lng }))
            .map(item => {
                const marker = new AdvancedMarkerElement({
                    map: mapRef.current,
                    position: { lat: item.lat, lng: item.lng },
                    content: createBadge(item),
                    gmpClickable: !!onMarkerClickRef.current,
                });
                if (onMarkerClickRef.current) {
                    marker.addEventListener('gmp-click', () => onMarkerClickRef.current?.(item));
                }
                return marker;
            });
        const positionsKey = items
            .filter(item => isValidCoordinate({ lat: item.lat, lng: item.lng }))
            .map(item => `${item.lat.toFixed(6)},${item.lng.toFixed(6)}`)
            .sort()
            .join(';');
        if (badgeMarkersRef.current.length && positionsKey !== fittedPositionsRef.current) {
            fittedPositionsRef.current = positionsKey;
            const bounds = new window.google.maps.LatLngBounds();
            badgeMarkersRef.current.forEach(marker => bounds.extend(marker.position));
            mapRef.current.fitBounds(bounds);
        }
    };

    const loadMap = async () => {
        try {
            const { Map } = await window.google.maps.importLibrary('maps');
            markerLibRef.current = await window.google.maps.importLibrary('marker');
            const { AdvancedMarkerElement } = markerLibRef.current;

            const map = new Map(document.getElementById(id), {
                ...options,
                center: centerRef.current,
                mapId: id,
                disableDefaultUI: true,
                zoomControl: true,
                zoomControlOptions: {
                    style: window.google.maps.ZoomControlStyle.SMALL,
                    position: window.google.maps.ControlPosition.RIGHT_BOTTOM,
                },
                mapTypeId: window.google.maps.MapTypeId.ROADMAP,
            });
            mapRef.current = map;

            if (markersRef.current) {
                renderBadgeMarkers(markersRef.current);
                onMapLoad(map, null);
                return;
            }

            const marker = new AdvancedMarkerElement({
                map,
                position: centerRef.current,
                gmpDraggable: pinDraggableRef.current,
            });

            markerRef.current = marker;

            const infoWindow = new window.google.maps.InfoWindow();
            marker.addListener('dragend', event => {
                const position = event.latLng;
                const latitude = position.lat();
                const longitude = position.lng();
                const latitudeShort = Number(latitude).toFixed(6);
                const longitudeShort = Number(longitude).toFixed(6);

                draggedCenterRef.current = { lat: latitude, lng: longitude };
                onChange({ latitude, longitude });

                infoWindow.close();
                infoWindow.setContent(`Pin dropped at: ${latitudeShort}, ${longitudeShort}`);
                infoWindow.open(marker.map, marker);
            });

            onMapLoad(map, marker);
        } catch (error) {
            handleApiError(error);
        }
    };

    useEffect(() => {
        loadMap();
    }, []);

    useEffect(() => {
        if (!mapRef.current || !markerLibRef.current || !markers) return;
        renderBadgeMarkers(markers);
    }, [markers]);

    useEffect(() => {
        if (markerRef.current) {
            markerRef.current.gmpDraggable = pinDraggable;
        }
    }, [pinDraggable]);

    useEffect(() => {
        const { center } = options;
        if (!mapRef.current || !markerRef.current || !isValidCoordinate(center)) return;

        const dragged = draggedCenterRef.current;
        draggedCenterRef.current = null;
        if (dragged && dragged.lat === center.lat && dragged.lng === center.lng) return;

        mapRef.current.setCenter(center);
        markerRef.current.position = center;
    }, [options.center?.lat, options.center?.lng]);

    return <Container id={id} sx={style} />;
};

export default GoogleMap;
